import { snackbarError, snackbarInfo } from 'reusable/mui/snackbar/snackbar'
import { makeAutoObservable } from 'mobx'
import Loader from 'reusable/mobx-helpers/Loader'
import { AuthError, confirmSignIn, forgetDevice, rememberDevice, signIn } from 'aws-amplify/auth'
import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import CognitoSignUpException from '@gap/utils/auth/CognitoSignUpException'
import { AuthVerificationProps } from './AuthVerification'
import AuthStore from 'auth/AuthStore'
import AuthAnalytics from 'analytics/AuthAnalytics'

interface AuthVerificationStoreProps extends AuthVerificationProps {
   authStore: AuthStore
}

export default class AuthVerificationStore {
   loader = new Loader(false)
   resendCodeLoader = new Loader(false)
   sp
   fields = {
      confirmationCode: '',
   }

   constructor(sp: AuthVerificationStoreProps) {
      this.sp = sp
      makeAutoObservable(this)
   }

   get formHandler() {
      return formHandler(this.fields, {
         confirmationCode: { type: 'string' },
      } satisfies FormHandlerConfig)
   }

   get email() {
      if (this.sp.email) {
         return this.sp.email
      }
      if (!this.sp.loginFormFields) {
         return null
      }
      return 'email' in this.sp.loginFormFields ? this.sp.loginFormFields.email : this.sp.loginFormFields.username
   }

   get canClickVerify() {
      return this.fields.confirmationCode.length === 6
   }

   get canGoBack() {
      // Only allow the user to go back when verifying when they aren't verifying their account on trial signup.
      // Otherwise we end up with a user and no org and we dont handle it
      return !window.location.pathname.includes('trial')
   }

   backToLogin = () => {
      if (this.sp.customHandlers?.backToLogin) {
         this.sp.customHandlers.backToLogin()
         return
      }

      this.sp.authStore.status = 'sign in'
   }

   resendCode = async () => {
      if (this.sp.customHandlers?.resendCode) {
         await this.sp.customHandlers.resendCode()
         return
      }

      this.resendCodeLoader.executeLoading(async () => {
         try {
            if (!this.email || !this.sp.loginFormFields) {
               snackbarError('Unable to resend code. Please reenter your details.')
               return
            }

            await signIn({
               username: this.email,
               password: this.sp.loginFormFields.password
            })

            this.fields.confirmationCode = ''
            snackbarInfo('A new code has been sent to your email address')
         }
         catch (error) {
            if (!(error instanceof Error)) {
               snackbarError('An unexpected error occurred')
               return
            }

            if (error.name === CognitoSignUpException.LimitExceededException) {
               snackbarError('Too many attempts. Please try again later.')
               return
            }

            snackbarError(error.message || 'An error occurred during verification')
         }
      })
   }

   /**
    * This is the flow that is used to verify the code and sign in the user when logging in
    */
   verifyCode = async () => {
      await this.loader.executeLoading(async () => {
         try {
            const result = await confirmSignIn({
               challengeResponse: this.fields.confirmationCode
            })

            if (result.nextStep.signInStep === 'DONE') {
               if (this.sp.authStore.fields.rememberDevice) {
                  await rememberDevice()
               } else {
                  await forgetDevice()
               }
            } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
               this.fields.confirmationCode = ''
               AuthAnalytics.verificationCodeResent()
               return snackbarError('Invalid verification code. For security reasons we have sent you a new code.')
            }

            await this.sp.onVerify(result)
         } catch (error: any) {
            if (error instanceof AuthError) {
               switch (error.name) {
                  case 'CodeMismatchException':
                     return snackbarError('Invalid verification code')
                  case 'ExpiredCodeException':
                     return snackbarError('Verification code has expired')
                  default:
                     return snackbarError(`Unknown error: ${error.message}`)
               }
            }

            const message = error instanceof Error ? error.message || `${error}` : `${error}`
            snackbarError(message)
         }
      })
   }
}
